/**
 * « Sante du coffre » (Watchtower-like), 100 % locale : s'appuie sur
 * `analyzePasswordHygiene` de `@klavox/vault-model` et ajoute l'anciennete,
 * l'absence de TOTP sur les sites qui le proposent, les passkeys disponibles, les sites non chiffres, les sites
 * compromis depuis le dernier changement du mot de passe
 * (`http://`), les cartes bancaires, les pieces d'identite, les cles d'API, les licences logicielles et les cartes de
 * membre expirees ou proches de l'expiration, les mots de passe dont l'echeance de changement fixee par
 * l'utilisateur est passee ou proche (`password-expiry.ts`), les entrees en double et le
 * resultat (deja obtenu) de `breach-check.ts` sur les fuites connues. Les entrees exclues par
 * l'utilisateur (`health-exclusion.ts`) sortent des alertes et du score, pas de la detection de
 * reutilisation des autres ; le rapport les liste a part. Une alerte ignoree une a une (faible, reutilise, ancien,
 * non chiffre, 2FA disponible) se tait de meme pour cette entree seulement, listee elle aussi. Zero E/S, zero reseau :
 * le rapport ne contient JAMAIS de secret (identifiants d'entree, compteurs,
 * indicateurs) — les noms affiches sont relus par l'UI dans les entrees
 * dechiffrees en memoire.
 */
import {
  WEAK_PASSWORD_BITS_THRESHOLD,
  analyzePasswordHygiene,
  equivalentDomainsOf,
  estimatePasswordStrengthBits,
  passwordEntryWords,
  isSameSite,
  registrableDomain,
  renamedSiteOf,
  siteHostname,
  type SiteMatchSecrets,
  type VaultItem,
} from '@klavox/vault-model';
import { strengthLabel } from '../core/generator.js';
import { BREACHED_SITES } from './breached-sites-data.js';
import { CHANGE_PASSWORD_URLS } from './change-password-urls-data.js';
import { engagedSites, findDeceptiveUri, type EngagedSites } from './deceptive-host.js';
import { isArchived } from './entry-archive.js';
import { isHealthExcluded, readIgnoredHealthAlerts, type IgnorableHealthAlert } from './health-exclusion.js';
import { readPasskeys } from './passkey-credential.js';
import { hasPasskeyElsewhere } from './passkey-elsewhere.js';
import { PASSKEYS_DIRECTORY_SITES } from './passkeys-directory-data.js';
import { passwordExpiryState, readPasswordExpiry } from './password-expiry.js';
import { lastPasswordChange } from './password-history.js';
import { TWOFA_DIRECTORY_TOTP } from './twofa-directory-data.js';
import { IDENTITY_DOCUMENTS, emailAccountInClear, isCardExpired, isDocumentExpired, isMembershipExpired, readTypedFields, type IdentityDocument } from './typed-fields.js';

/** Au-dela, un mot de passe est considere ancien (a renouveler). */
export const OLD_PASSWORD_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** Une carte qui expire dans ce delai est a renouveler (« Expiring » de 1Password Watchtower). */
export const CARD_EXPIRY_WARNING_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * Une piece d'identite qui expire dans ce delai est a renouveler (« Expiring » de 1Password Watchtower). Six mois,
 * pas soixante jours : de nombreux pays exigent un passeport valable six mois au-dela du sejour, et un
 * renouvellement en mairie ou en prefecture se compte en semaines.
 */
export const DOCUMENT_EXPIRY_WARNING_MS = 182 * 24 * 60 * 60 * 1000;

/**
 * Une cle d'API qui expire dans ce delai est a renouveler. Trente jours : de quoi emettre la nouvelle cle, la deployer
 * partout ou l'ancienne sert et revoquer l'ancienne, avant que le service ne tombe a l'echeance.
 */
export const API_KEY_EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Une licence logicielle (abonnement, maintenance) qui expire dans ce delai est a renouveler. Trente jours : le delai
 * de preavis courant d'un abonnement annuel, de quoi renouveler -- ou resilier -- avant le prelevement ou la coupure.
 */
export const SOFTWARE_LICENSE_EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Une carte de membre ou de fidelite qui expire dans ce delai est a renouveler. Trente jours : le preavis courant d'une
 * cotisation annuelle, et le delai qu'un programme de fidelite laisse en general pour garder son statut et ses points.
 */
export const MEMBERSHIP_EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Sites proposant un second facteur TOTP, indexes par domaine enregistrable
 * (eTLD+1) → lien https « comment l'activer » (`''` si la source n'en donne
 * pas). Source : export TOTP complet de 2fa.directory, embarque et versionne
 * (`twofa-directory-data.ts`) -- le coffre n'interroge jamais ce service.
 */
export type TotpDirectory = ReadonlyMap<string, string>;

/** Le lien finit dans un `href` : rien d'autre que du https, meme si la liste generee l'a deja filtre. */
function httpsLink(link: string): string {
  return link.startsWith('https://') ? link : '';
}

/** Construit l'index a partir du format genere (`domaine<TAB>lien`, une ligne par domaine). */
export function indexTotpDirectory(data: string): TotpDirectory {
  const index = new Map<string, string>();
  for (const line of data.split('\n')) {
    const [domain = '', link = ''] = line.split('\t');
    const site = registrableDomain(domain);
    if (site === null) continue;
    const documentation = httpsLink(link);
    // Le service dont le domaine EST le site donne le lien (`google.com`) ; a defaut, le premier lien connu.
    const current = index.get(site);
    if (current === undefined || (documentation !== '' && (current === '' || domain === site))) index.set(site, documentation);
  }
  return index;
}

let defaultDirectory: TotpDirectory | null = null;

/** L'index de la liste embarquee, construit une seule fois. */
export function defaultTotpDirectory(): TotpDirectory {
  return (defaultDirectory ??= indexTotpDirectory(TWOFA_DIRECTORY_TOTP));
}

/** Ce qu'un site permet de faire d'une passkey, et ou lire comment la creer. */
export interface PasskeySupport {
  /** Connexion sans mot de passe. */
  readonly passwordless: boolean;
  /** Second facteur, apres le mot de passe. */
  readonly mfa: boolean;
  /** Lien https « comment creer une passkey » (`''` si la source n'en donne pas). */
  readonly documentation: string;
}

/**
 * Sites acceptant les passkeys, indexes par domaine enregistrable (eTLD+1), comme la liste TOTP : la passkey
 * d'un compte Google sert sur `accounts.google.com` meme si la source cite `mail.google.com`. Source :
 * passkeys.directory, embarque et versionne (`passkeys-directory-data.ts`) -- le coffre n'interroge jamais ce
 * service.
 */
export type PasskeyDirectory = ReadonlyMap<string, PasskeySupport>;

/** Construit l'index a partir du format genere (`domaine<TAB>usages<TAB>lien`, une ligne par domaine). */
export function indexPasskeyDirectory(data: string): PasskeyDirectory {
  const index = new Map<string, PasskeySupport>();
  for (const line of data.split('\n')) {
    const [domain = '', usages = '', link = ''] = line.split('\t');
    const site = registrableDomain(domain);
    const passwordless = usages.includes('p');
    const mfa = usages.includes('m');
    // Une ligne qui n'annonce aucun usage n'affirme rien.
    if (site === null || !(passwordless || mfa)) continue;
    const documentation = httpsLink(link);
    const current = index.get(site);
    // Les usages des sous-domaines d'un site s'additionnent ; le lien vient du domaine qui EST le site, a defaut
    // du premier lien connu.
    index.set(site, {
      passwordless: passwordless || current?.passwordless === true,
      mfa: mfa || current?.mfa === true,
      documentation:
        current === undefined || (documentation !== '' && (current.documentation === '' || domain === site)) ? documentation : current.documentation,
    });
  }
  return index;
}

let defaultPasskeys: PasskeyDirectory | null = null;

/** L'index de la liste embarquee, construit une seule fois. */
export function defaultPasskeyDirectory(): PasskeyDirectory {
  return (defaultPasskeys ??= indexPasskeyDirectory(PASSKEYS_DIRECTORY_SITES));
}

/** Fuite d'un site qui a emporte des mots de passe (source : Have I Been Pwned, `breached-sites-data.ts`). */
export interface SiteBreach {
  /** Nom de la fuite, tel que la source le publie (« Adobe »). */
  readonly title: string;
  /** Jour de la fuite, `AAAA-MM-JJ`. */
  readonly breachDate: string;
}

/**
 * Sites dont une fuite a emporte des mots de passe, indexes par domaine TEL QUE LA SOURCE LE CITE (pas ramene au
 * domaine enregistrable : la fuite du forum `forums.boxee.com` ne dit rien du compte `app.boxee.com`). Embarque et
 * versionne -- le coffre n'interroge jamais ce service.
 */
export type BreachedSiteDirectory = ReadonlyMap<string, SiteBreach>;

/** Construit l'index a partir du format genere (`domaine<TAB>AAAA-MM-JJ<TAB>nom`, une ligne par domaine). */
export function indexBreachedSites(data: string): BreachedSiteDirectory {
  const index = new Map<string, SiteBreach>();
  for (const line of data.split('\n')) {
    const [domain = '', breachDate = '', title = ''] = line.split('\t');
    const host = domain.trim().toLowerCase();
    // Une date illisible n'affirme rien.
    if (host === '' || !/^\d{4}-\d{2}-\d{2}$/u.test(breachDate) || Number.isNaN(Date.parse(breachDate))) continue;
    const current = index.get(host);
    if (current === undefined || breachDate > current.breachDate) index.set(host, { title: title || host, breachDate });
  }
  return index;
}

let defaultBreaches: BreachedSiteDirectory | null = null;

/** L'index de la liste embarquee, construit une seule fois. */
export function defaultBreachedSites(): BreachedSiteDirectory {
  return (defaultBreaches ??= indexBreachedSites(BREACHED_SITES));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Fuite du site de l'entree survenue APRES le dernier changement de son mot de passe (« Compromised websites » de 1Password
 * Watchtower), `null` sinon. Le dernier changement est le plus recent de l'historique et de la date importee
 * (`lastPasswordChange`) ; sans l'un ni l'autre, le mot de passe n'a pas change depuis la creation de l'entree. Une fuite ne date que d'un jour : un changement fait ce jour-la ne prouve pas
 * qu'il l'a suivie -- l'entree reste signalee. Le site de l'entree et ses domaines equivalents (sauf opt-out) sont
 * compares a la fuite, puis chacun de leurs domaines parents jusqu'au domaine enregistrable : la fuite d'`adobe.com`
 * vaut pour `account.adobe.com`, jamais l'inverse.
 */
export function siteBreachSinceChange(entry: VaultItem, directory: BreachedSiteDirectory = defaultBreachedSites()): SiteBreach | null {
  const lastChange = lastPasswordChange(entry.secrets, entry.createdAt);
  const secrets = entry.secrets;
  for (const uri of secrets.uris ?? []) {
    const equivalents = secrets.equivalentDomains === false ? [] : equivalentDomainsOf(uri).map((domain) => `https://${domain}`);
    for (const candidate of [uri, ...equivalents]) {
      const host = siteHostname(candidate);
      const site = host === null ? null : registrableDomain(host);
      if (host === null || site === null) continue;
      for (let name = host; ; name = name.slice(name.indexOf('.') + 1)) {
        const breach = directory.get(name);
        if (breach !== undefined && lastChange < Date.parse(breach.breachDate) + DAY_MS) return breach;
        if (name === site || !name.includes('.')) break;
      }
    }
  }
  return null;
}

/**
 * Adresse ou le mot de passe circule en clair (« Unsecured websites » de
 * Bitwarden et de 1Password Watchtower). Seul le schema `http:` explicite
 * compte : une adresse sans schema n'affirme rien. La boucle locale
 * (`localhost`, `127.0.0.0/8`, `[::1]`) est exclue, son trafic ne quitte
 * jamais la machine ; un reseau local, lui, reste signale.
 */
export function isInsecureUri(uri: string): boolean {
  let url: URL;
  try {
    url = new URL(uri.trim());
  } catch {
    return false;
  }
  if (url.protocol !== 'http:') return false;
  const host = url.hostname.toLowerCase();
  return !(host === 'localhost' || host.endsWith('.localhost') || /^127(\.\d{1,3}){3}$/u.test(host) || host === '[::1]');
}

/**
 * Adresses ou le mot de passe de l'entree est saisi : ses sites, et pour un serveur l'adresse du serveur et sa console
 * d'administration (une console en `http://` fait circuler le mot de passe d'administration en clair).
 */
function entryAddresses(entry: VaultItem): string[] {
  const uris = [...(entry.secrets.uris ?? [])];
  if (entry.type !== 'server') return uris;
  const server = readTypedFields(entry.secrets, 'server');
  return [...uris, ...(['serverUrl', 'adminConsoleUrl'] as const).map((key) => server[key] ?? '').filter((uri) => uri.length > 0)];
}

const vaultSitesCache = new WeakMap<readonly VaultItem[], EngagedSites>();

/**
 * Les sites du coffre (adresses des entrees actives) : un nom qui enchasse l'un d'eux (`mabanque.fr.connexion-securisee.net`)
 * est une adresse trompeuse, comme celui qui enchasse un domaine tres visite. Calcule une fois par etat du coffre.
 */
export function vaultSites(entries: readonly VaultItem[]): EngagedSites {
  let sites = vaultSitesCache.get(entries);
  if (sites === undefined) {
    const hosts = entries.filter((entry) => entry.deletedAt === null).flatMap((entry) => entryAddresses(entry).map((uri) => siteHostname(uri) ?? ''));
    sites = engagedSites(hosts.filter((host) => host !== ''));
    vaultSitesCache.set(entries, sites);
  }
  return sites;
}

/**
 * Page « changer le mot de passe » du site : `/.well-known/change-password` (W3C, « A Well-Known URL for Changing
 * Passwords »), que le site redirige vers son formulaire -- le bouton « Changer » de 1Password Watchtower, de Chrome et de
 * Safari. Seule une adresse `https:` de l'entree en donne une (on n'envoie pas saisir un nouveau mot de passe en clair),
 * et aucune si une adresse de l'entree imite un autre site. L'origine sort de `URL` : punycode, sans identifiants.
 * Un site sans well-known connu de la table de repli d'Apple (celle de Safari) y prend sa page : l'hote exact, a
 * defaut un de ses domaines parents jusqu'au domaine enregistrable (`www.amazon.co.uk` -> `amazon.co.uk`, jamais
 * `co.uk`).
 */
export function changePasswordUrl(
  entry: VaultItem,
  quirks: ReadonlyMap<string, string> = defaultChangePasswordQuirks(),
  /** Les sites du coffre (`vaultSites`) : une adresse qui en enchasse un n'a pas de page de changement. */
  sites: EngagedSites = engagedSites([]),
): string | null {
  if (findDeceptiveUri(entryAddresses(entry), sites) !== null) return null;
  for (const uri of entry.secrets.uris ?? []) {
    let site: URL;
    try {
      site = new URL(uri.trim());
    } catch {
      continue;
    }
    if (site.protocol !== 'https:' || site.hostname === '') continue;
    const floor = registrableDomain(site.hostname) ?? site.hostname;
    for (let host = site.hostname; host.length >= floor.length; host = host.slice(host.indexOf('.') + 1)) {
      const quirk = quirks.get(host);
      if (quirk !== undefined) return quirk;
      if (!host.includes('.')) break;
    }
    return `${site.origin}/.well-known/change-password`;
  }
  return null;
}

/** Construit l'index de repli a partir du format genere (`domaine<TAB>adresse https`, une ligne par domaine). */
export function indexChangePasswordQuirks(data: string): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const line of data.split('\n')) {
    const [domain = '', link = ''] = line.split('\t');
    const url = httpsLink(link);
    if (domain !== '' && url !== '') index.set(domain, url);
  }
  return index;
}

let defaultQuirks: ReadonlyMap<string, string> | null = null;

/** L'index de la table embarquee, construit une seule fois. */
export function defaultChangePasswordQuirks(): ReadonlyMap<string, string> {
  return (defaultQuirks ??= indexChangePasswordQuirks(CHANGE_PASSWORD_URLS));
}

/**
 * Le mot de passe de l'entree circule-t-il en clair ? Un site en `http://`, ou un compte de messagerie dont un serveur
 * est configure sans securite (identifiant et mot de passe envoyes tels quels a chaque releve ou envoi).
 */
function entryInClear(entry: VaultItem): boolean {
  if (entryAddresses(entry).some(isInsecureUri)) return true;
  return entry.type === 'email' && emailAccountInClear(readTypedFields(entry.secrets, 'email'));
}

/**
 * Mot de passe d'un SECOND compte porte par l'entree : la console d'administration d'un serveur, le compte SMTP d'une
 * messagerie quand il differe du compte entrant. C'est un vrai mot de passe, saisi ailleurs que celui de l'entree.
 */
export type SecondaryPasswordField = 'adminPassword' | 'smtpPassword';

/** Constat sur un mot de passe secondaire : le champ, jamais la valeur. */
export interface VaultSecondaryPasswordIssue {
  readonly field: SecondaryPasswordField;
  readonly weak: boolean;
  /** Le meme mot de passe ouvre aussi une AUTRE entree (compte ou secondaire). */
  readonly reused: boolean;
  /** Occurrences dans les fuites connues (0 = non trouve ou non verifie). */
  readonly breachCount: number;
}

/**
 * Mots de passe secondaires d'une entree. Identique au mot de passe de l'entree, ce n'en est pas un second : il partage
 * son constat.
 */
function secondaryPasswords(entry: VaultItem): { field: SecondaryPasswordField; password: string }[] {
  const found: { field: SecondaryPasswordField; password: string }[] = [];
  if (entry.type === 'server') found.push({ field: 'adminPassword', password: readTypedFields(entry.secrets, 'server')['adminPassword'] ?? '' });
  if (entry.type === 'email') found.push({ field: 'smtpPassword', password: readTypedFields(entry.secrets, 'email')['smtpPassword'] ?? '' });
  return found.filter(({ password }) => password.length > 0 && password !== entry.secrets.password);
}

/**
 * Mots de passe que la Sante du coffre analyse, ceux des entrees non supprimees : le mot de passe de chaque entree et
 * ses mots de passe secondaires. C'est la liste que « Verifier les fuites » soumet (k-anonymat, `breach-check.ts`).
 */
export function healthPasswords(entries: readonly VaultItem[]): string[] {
  return entries
    .filter((entry) => entry.deletedAt === null)
    .flatMap((entry) => [entry.secrets.password ?? '', ...secondaryPasswords(entry).map(({ password }) => password)])
    .filter((password) => password.length > 0);
}

/**
 * Mots du titulaire du coffre, lus pour TOUTES ses entrees : la partie locale de l'e-mail du compte Klavox (comme
 * l'identifiant d'une entree). Qui vise un compte de cette personne connait son adresse.
 */
export function accountPasswordWords(accountEmail: string): string[] {
  return accountEmail.trim() === '' ? [] : passwordEntryWords({ username: accountEmail.trim() });
}

/**
 * Jauge de l'editeur, a la frappe : la meme estimation que la Sante du coffre, nom, identifiant et sites de l'entree
 * et e-mail du compte compris — « faible » ici l’est la-bas. `null` sans mot de passe ; jamais le mot de passe dans le message.
 */
export function draftPasswordStrength(
  password: string,
  username: string,
  uris: readonly string[],
  name = '',
  accountEmail = '',
): { readonly label: ReturnType<typeof strengthLabel>; readonly bits: number; readonly message: string } | null {
  if (password.length === 0) return null;
  const entryWords = passwordEntryWords({ name, username, uris });
  const entryBits = estimatePasswordStrengthBits(password, entryWords);
  const bits = estimatePasswordStrengthBits(password, [...entryWords, ...accountPasswordWords(accountEmail)]);
  const label = bits < WEAK_PASSWORD_BITS_THRESHOLD ? 'faible' : strengthLabel(bits);
  // Ne nomme que ce que le mot de passe reprend vraiment : les mots de l'entree, l'e-mail du compte, ou les deux.
  const reprises = [
    ...(entryBits < estimatePasswordStrengthBits(password) ? [name === '' ? 'l’identifiant ou le site' : 'le nom, l’identifiant ou le site'] : []),
    ...(bits < entryBits ? ['l’e-mail du compte'] : []),
  ];
  const fromEntry = reprises.length === 0 ? '' : `, reprend ${reprises.join(' et ')}`;
  return { label, bits, message: `Robustesse ${label} : ${Math.round(bits)} bits estimes${fromEntry}` };
}

/** Pour chaque mot de passe, le nombre d'entrees qui le portent (compte ou secondaire) : au-dela d'une, il est reutilise. */
function passwordHolders(entries: readonly VaultItem[]): Map<string, number> {
  const holders = new Map<string, number>();
  for (const entry of entries) {
    const passwords = new Set([entry.secrets.password ?? '', ...secondaryPasswords(entry).map(({ password }) => password)]);
    passwords.delete('');
    for (const password of passwords) holders.set(password, (holders.get(password) ?? 0) + 1);
  }
  return holders;
}

export interface VaultHealthIssue {
  readonly itemId: string;
  readonly weak: boolean;
  readonly reused: boolean;
  readonly old: boolean;
  /** Occurrences du mot de passe dans les fuites connues (0 = non trouve ou non verifie). */
  readonly breachCount: number;
  /** Site connu pour proposer le TOTP, mais aucune graine enregistree dans l'entree. */
  readonly missingTotp: boolean;
  /** Lien https « comment activer la 2FA » sur ce site, quand `missingTotp` et que la source en donne un. */
  readonly totpDocumentation: string | null;
  /** Au moins une adresse de l'entree est en `http://` : le mot de passe y circule en clair. */
  readonly insecureUri: boolean;
  /** Au moins une adresse de l'entree imite un autre site avec des lettres d'un autre alphabet (`deceptive-host.ts`). */
  readonly deceptiveUri: boolean;
  /** Fuite du site posterieure au dernier changement du mot de passe (`siteBreachSinceChange`), `null` sinon. */
  readonly siteBreach: SiteBreach | null;
  /**
   * Mot de passe a changer (fuite, faible, reutilise, ancien, site compromis) : l'IHM propose alors `changePasswordUrl`.
   * Un booleen, pas le lien : le rapport ne porte aucune adresse.
   */
  readonly mustChange: boolean;
  /** Mots de passe secondaires faibles, reutilises ou fuites (console, SMTP) ; vide sinon. Hors `mustChange` : la page de changement du site ne les concerne pas. */
  readonly secondaryPasswords: readonly VaultSecondaryPasswordIssue[];
}

/** Carte bancaire a renouveler : expiree, ou expirant dans `CARD_EXPIRY_WARNING_MS`. */
export interface VaultCardIssue {
  readonly itemId: string;
  /** `true` : deja expiree ; `false` : expire bientot. */
  readonly expired: boolean;
}

/**
 * Le site accepte les passkeys, et l'entree n'en porte aucune (« Passkeys available » de 1Password Watchtower).
 * Une recommandation, pas un defaut du mot de passe : hors score, comme les cartes.
 */
export interface VaultPasskeyIssue {
  readonly itemId: string;
  readonly passwordless: boolean;
  readonly mfa: boolean;
  /** Lien https « comment creer une passkey » sur ce site, `null` si la source n'en donne pas. */
  readonly documentation: string | null;
}

/**
 * Adresse sur un domaine que son service a quitte (`fromDomainsAreObsoleted` de la table d'Apple : `twitter.com` →
 * `x.com`). L'identifiant reste reconnu sur le nouveau domaine (sens unique de `isEquivalentSite`) ; mettre l'adresse a
 * jour rend a l'entree son icone, son lien et les tables de la Sante (TOTP, passkeys, fuites) du nom actuel. Hors score.
 */
export interface VaultRenamedSiteIssue {
  readonly itemId: string;
  /** Domaine abandonne, tel que la table le nomme. */
  readonly from: string;
  /** Domaine actuel du service. */
  readonly to: string;
}

/** Cle d'API a renouveler : expiree, ou expirant dans `API_KEY_EXPIRY_WARNING_MS`. */
export interface VaultApiKeyIssue {
  readonly itemId: string;
  /** `true` : deja expiree ; `false` : expire bientot. */
  readonly expired: boolean;
}

/** Licence logicielle a renouveler : expiree, ou expirant dans `SOFTWARE_LICENSE_EXPIRY_WARNING_MS`. */
export interface VaultLicenseIssue {
  readonly itemId: string;
  /** `true` : deja expiree ; `false` : expire bientot. */
  readonly expired: boolean;
}

/** Carte de membre a renouveler : expiree, ou expirant dans `MEMBERSHIP_EXPIRY_WARNING_MS`. */
export interface VaultMembershipIssue {
  readonly itemId: string;
  /** `true` : deja expiree ; `false` : expire bientot. */
  readonly expired: boolean;
}

/** Mot de passe a changer : echeance fixee sur l'entree passee, ou dans `PASSWORD_EXPIRY_WARNING_MS`. */
export interface VaultPasswordExpiryIssue {
  readonly itemId: string;
  /** `true` : echeance passee ; `false` : echeance proche. */
  readonly expired: boolean;
}

/** Piece d'identite a renouveler : expiree, ou expirant dans `DOCUMENT_EXPIRY_WARNING_MS`. */
export interface VaultDocumentIssue {
  readonly itemId: string;
  readonly document: IdentityDocument;
  /** `true` : deja expiree ; `false` : expire bientot. */
  readonly expired: boolean;
}

export interface VaultHealthReport {
  /** Entrees actives portant un mot de passe, ou seulement un mot de passe secondaire (base du score). */
  readonly analyzed: number;
  readonly weak: number;
  readonly reused: number;
  readonly old: number;
  /** Entrees dont le mot de passe figure dans une fuite connue (0 si la verification n'a pas ete faite). */
  readonly breached: number;
  readonly missingTotp: number;
  readonly insecureUri: number;
  /** Entrees dont une adresse imite un autre site (homographe IDN). */
  readonly deceptiveUri: number;
  /** Entrees dont le site a fuite apres le dernier changement du mot de passe. */
  readonly compromisedSite: number;
  /** Entrees dont le site accepte les passkeys, sans passkey enregistree (`passkeyIssues.length`). */
  readonly passkeyAvailable: number;
  /** Entrees dont une adresse est sur un domaine abandonne par son service (`renamedSiteIssues.length`). */
  readonly renamedSites: number;
  /** Mots de passe (hors archives) dont l'echeance de changement est passee. */
  readonly expiredPasswords: number;
  /** Mots de passe (hors archives) dont l'echeance de changement tombe dans `PASSWORD_EXPIRY_WARNING_MS`. */
  readonly expiringPasswords: number;
  /** Cartes actives (hors archives) deja expirees. */
  readonly expiredCards: number;
  /** Cartes actives (hors archives) expirant dans `CARD_EXPIRY_WARNING_MS`. */
  readonly expiringCards: number;
  /** Pieces d'identite (hors archives) deja expirees. */
  readonly expiredDocuments: number;
  /** Pieces d'identite (hors archives) expirant dans `DOCUMENT_EXPIRY_WARNING_MS`. */
  readonly expiringDocuments: number;
  /** Cles d'API (hors archives) deja expirees. */
  readonly expiredApiKeys: number;
  /** Cles d'API (hors archives) expirant dans `API_KEY_EXPIRY_WARNING_MS`. */
  readonly expiringApiKeys: number;
  /** Licences logicielles (hors archives) deja expirees. */
  readonly expiredLicenses: number;
  /** Licences logicielles (hors archives) expirant dans `SOFTWARE_LICENSE_EXPIRY_WARNING_MS`. */
  readonly expiringLicenses: number;
  /** Cartes de membre (hors archives) deja expirees. */
  readonly expiredMemberships: number;
  /** Cartes de membre (hors archives) expirant dans `MEMBERSHIP_EXPIRY_WARNING_MS`. */
  readonly expiringMemberships: number;
  /** 0-100, `null` si aucun mot de passe a analyser. */
  readonly score: number | null;
  /** Entrees a traiter, les plus graves d'abord. */
  readonly issues: readonly VaultHealthIssue[];
  /**
   * Mots de passe a changer, echeances passees d'abord. Hors score, comme les cartes : l'echeance est une regle que
   * l'utilisateur s'est donnee, pas une faiblesse du mot de passe.
   */
  readonly passwordExpiryIssues: readonly VaultPasswordExpiryIssue[];
  /** Cartes a renouveler, les expirees d'abord. Hors score : le score note les mots de passe. */
  readonly cardIssues: readonly VaultCardIssue[];
  /** Pieces d'identite a renouveler, les expirees d'abord. Hors score, comme les cartes. */
  readonly documentIssues: readonly VaultDocumentIssue[];
  /** Cles d'API a renouveler, les expirees d'abord. Hors score, comme les cartes. */
  readonly apiKeyIssues: readonly VaultApiKeyIssue[];
  /** Licences logicielles a renouveler, les expirees d'abord. Hors score, comme les cartes. */
  readonly licenseIssues: readonly VaultLicenseIssue[];
  /** Cartes de membre a renouveler, les expirees d'abord. Hors score, comme les cartes. */
  readonly membershipIssues: readonly VaultMembershipIssue[];
  /** Entrees dont le site accepte les passkeys, connexion sans mot de passe d'abord. Hors score. */
  readonly passkeyIssues: readonly VaultPasskeyIssue[];
  /** Entrees dont une adresse est sur un domaine abandonne, dans l'ordre du coffre. Hors score. */
  readonly renamedSiteIssues: readonly VaultRenamedSiteIssue[];
  /** Groupes d'entrees en double, les plus nombreux d'abord. Hors score. */
  readonly duplicateGroups: readonly VaultDuplicateGroup[];
  /** Copies en trop (somme, par groupe, des entrees au-dela de la premiere). */
  readonly duplicates: number;
  /** Entrees exclues par l'utilisateur : ni alerte, ni score, mais listees -- jamais escamotees. */
  readonly excludedIds: readonly string[];
  /**
   * Alertes ignorees une a une sur des entrees surveillees (hors entrees exclues, deja listees a part) : ni badge, ni
   * compteur, ni score pour ces alertes-la, mais listees -- jamais escamotees.
   */
  readonly ignoredAlerts: readonly VaultIgnoredAlerts[];
}

/** Alertes que l'utilisateur a ignorees sur une entree surveillee : elles se taisent, mais restent listees. */
export interface VaultIgnoredAlerts {
  readonly itemId: string;
  readonly alerts: readonly IgnorableHealthAlert[];
}

/**
 * Entrees en double : meme type, meme site, meme identifiant, meme mot de passe -- le plus souvent un import fait deux
 * fois. `itemIds` va de la plus recemment modifiee (celle qu'on garde d'ordinaire) a la plus ancienne. Hors score : un
 * doublon n'expose rien de plus, il encombre et fait passer son mot de passe pour reutilise.
 */
export interface VaultDuplicateGroup {
  readonly itemIds: readonly string[];
}

/** Penalites par entree (score de l'entree = 100 - somme, plancher 0). */
const PENALTY = { breached: 60, weak: 50, reused: 35, old: 10, missingTotp: 10, insecureUri: 20, deceptiveUri: 60, compromisedSite: 50 } as const;

/**
 * Valeur de l'index (TOTP, passkeys) pour le premier site de l'entree qui y figure, `undefined` si aucun. Meme site,
 * ou domaine equivalent sauf opt-out de l'entree (memes regles que `entryMatchesSite`) : une entree `bing.com`
 * profite du TOTP du compte Microsoft. Une recherche par adresse dans l'index, pas un parcours de la liste : 1 600
 * domaines par entree figeraient un gros coffre.
 */
function lookupSite<T>(secrets: SiteMatchSecrets, directory: ReadonlyMap<string, T>): T | undefined {
  for (const uri of secrets.uris ?? []) {
    const candidates = secrets.equivalentDomains === false ? [uri] : [uri, ...equivalentDomainsOf(uri).map((domain) => `https://${domain}`)];
    for (const candidate of candidates) {
      const host = siteHostname(candidate);
      const site = host === null ? null : registrableDomain(host);
      if (site !== null && directory.has(site)) return directory.get(site);
    }
  }
  return undefined;
}

/**
 * Entrees dont le site accepte les passkeys. Une entree qui porte deja une passkey, ou dont l'utilisateur a
 * declare la passkey rangee ailleurs (cle de securite, trousseau du systeme), n'a plus rien a faire : la signaler
 * serait du bruit qu'aucun geste ne ferait taire.
 */
function passkeyIssues(entries: readonly VaultItem[], directory: PasskeyDirectory): VaultPasskeyIssue[] {
  const issues: VaultPasskeyIssue[] = [];
  for (const entry of entries) {
    if (readPasskeys(entry.secrets).length > 0 || hasPasskeyElsewhere(entry.secrets)) continue;
    const support = lookupSite(entry.secrets, directory);
    if (support === undefined) continue;
    issues.push({ itemId: entry.id, passwordless: support.passwordless, mfa: support.mfa, documentation: support.documentation || null });
  }
  // Se passer du mot de passe d'abord : c'est le gain le plus net contre l'hameconnage.
  return issues.sort((a, b) => Number(b.passwordless) - Number(a.passwordless));
}

/**
 * Entrees dont une adresse est sur un domaine abandonne (`renamedSiteOf`). Une entree archivee ou exclue ne leve aucune
 * alerte ; une entree retiree des domaines equivalents n'est reconnue que sur son adresse : lui proposer un autre
 * domaine contredirait ce choix.
 */
function renamedSiteIssues(entries: readonly VaultItem[]): VaultRenamedSiteIssue[] {
  const issues: VaultRenamedSiteIssue[] = [];
  for (const entry of entries) {
    if (isArchived(entry.secrets) || isHealthExcluded(entry.secrets) || entry.secrets.equivalentDomains === false) continue;
    for (const uri of entry.secrets.uris ?? []) {
      const renamed = renamedSiteOf(uri);
      if (renamed === null) continue;
      issues.push({ itemId: entry.id, from: renamed.from, to: renamed.to });
      break;
    }
  }
  return issues;
}

/**
 * Adresses de l'entree mises a jour : une adresse sur un domaine abandonne devient `https://<nouveau domaine>`, ou
 * disparait si l'entree porte deja une adresse du nouveau site (jamais deux fois le meme site). Le chemin de l'ancienne
 * adresse n'est pas repris : rien ne dit qu'il existe sous le nouveau nom. Les autres adresses restent telles quelles.
 */
export function withRenamedSiteAddresses(uris: readonly string[]): string[] {
  const next: string[] = [];
  for (const uri of uris) {
    const renamed = renamedSiteOf(uri);
    if (renamed === null) {
      next.push(uri);
      continue;
    }
    const target = `https://${renamed.to}`;
    if (!uris.some((other) => isSameSite(other, target)) && !next.includes(target)) next.push(target);
  }
  return next;
}

/**
 * Cartes a renouveler. Une carte archivee n'est plus utilisee : la signaler
 * serait du bruit. Une expiration illisible n'affirme rien.
 */
function cardIssues(entries: readonly VaultItem[], now: number): VaultCardIssue[] {
  const found: VaultCardIssue[] = [];
  for (const entry of entries) {
    if (entry.type !== 'card' || isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const expiry = readTypedFields(entry.secrets, 'card')['expiry'];
    if (expiry === undefined) continue;
    if (isCardExpired(expiry, now)) found.push({ itemId: entry.id, expired: true });
    else if (isCardExpired(expiry, now + CARD_EXPIRY_WARNING_MS)) found.push({ itemId: entry.id, expired: false });
  }
  return found.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Mots de passe a changer selon l'echeance fixee sur l'entree. Une entree archivee n'est plus utilisee, une entree
 * sans mot de passe n'a rien a changer : les signaler serait du bruit. Une date illisible n'affirme rien.
 */
function passwordExpiryIssues(entries: readonly VaultItem[], now: number): VaultPasswordExpiryIssue[] {
  const expiries: VaultPasswordExpiryIssue[] = [];
  for (const entry of entries) {
    if (isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    if (typeof entry.secrets.password !== 'string' || entry.secrets.password.length === 0) continue;
    const expiry = readPasswordExpiry(entry.secrets);
    const state = expiry === null ? null : passwordExpiryState(expiry, now);
    if (state === 'expired') expiries.push({ itemId: entry.id, expired: true });
    else if (state === 'soon') expiries.push({ itemId: entry.id, expired: false });
  }
  return expiries.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Pieces d'identite a renouveler : une entree « identite » peut en porter trois (passeport, carte, permis), chacune
 * signalee a part. Une identite archivee n'est plus utilisee ; une date illisible n'affirme rien.
 */
function documentIssues(entries: readonly VaultItem[], now: number): VaultDocumentIssue[] {
  const issues: VaultDocumentIssue[] = [];
  for (const entry of entries) {
    if (entry.type !== 'identity' || isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const fields = readTypedFields(entry.secrets, 'identity');
    for (const { document, expiryKey } of IDENTITY_DOCUMENTS) {
      const date = fields[expiryKey];
      if (date === undefined) continue;
      if (isDocumentExpired(date, now)) issues.push({ itemId: entry.id, document, expired: true });
      else if (isDocumentExpired(date, now + DOCUMENT_EXPIRY_WARNING_MS)) issues.push({ itemId: entry.id, document, expired: false });
    }
  }
  return issues.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Cles d'API a renouveler. Une cle archivee n'est plus deployee : la signaler serait du bruit. Une date illisible
 * n'affirme rien.
 */
function apiKeyIssues(entries: readonly VaultItem[], now: number): VaultApiKeyIssue[] {
  const keys: VaultApiKeyIssue[] = [];
  for (const entry of entries) {
    if (entry.type !== 'api-key' || isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const expiry = readTypedFields(entry.secrets, 'api-key')['apiExpiry'];
    if (expiry === undefined) continue;
    if (isDocumentExpired(expiry, now)) keys.push({ itemId: entry.id, expired: true });
    else if (isDocumentExpired(expiry, now + API_KEY_EXPIRY_WARNING_MS)) keys.push({ itemId: entry.id, expired: false });
  }
  return keys.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Licences logicielles a renouveler. Une licence archivee n'est plus utilisee : la signaler serait du bruit. Une date
 * illisible n'affirme rien ; une licence perpetuelle n'a pas de date.
 */
function licenseIssues(entries: readonly VaultItem[], now: number): VaultLicenseIssue[] {
  const licenses: VaultLicenseIssue[] = [];
  for (const entry of entries) {
    if (entry.type !== 'software-license' || isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const expiry = readTypedFields(entry.secrets, 'software-license')['softwareExpiry'];
    if (expiry === undefined) continue;
    if (isDocumentExpired(expiry, now)) licenses.push({ itemId: entry.id, expired: true });
    else if (isDocumentExpired(expiry, now + SOFTWARE_LICENSE_EXPIRY_WARNING_MS)) licenses.push({ itemId: entry.id, expired: false });
  }
  return licenses.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Cartes de membre a renouveler. Une carte archivee n'est plus utilisee : la signaler serait du bruit. Une date
 * illisible n'affirme rien ; une adhesion a vie n'a pas de date.
 */
function membershipIssues(entries: readonly VaultItem[], now: number): VaultMembershipIssue[] {
  const memberships: VaultMembershipIssue[] = [];
  for (const entry of entries) {
    if (entry.type !== 'membership' || isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const expiry = readTypedFields(entry.secrets, 'membership')['membershipExpiry'];
    if (expiry === undefined) continue;
    if (isMembershipExpired(expiry, now)) memberships.push({ itemId: entry.id, expired: true });
    else if (isMembershipExpired(expiry, now + MEMBERSHIP_EXPIRY_WARNING_MS)) memberships.push({ itemId: entry.id, expired: false });
  }
  return memberships.sort((a, b) => Number(b.expired) - Number(a.expired));
}

/**
 * Cle de rapprochement des doublons, `null` si l'entree n'en a pas : sans mot de passe, rien ne prouve que deux
 * entrees ouvrent le meme compte. Le site est le domaine enregistrable de la premiere adresse lisible
 * (`connexion.banque.fr` et `www.banque.fr` sont un meme site) ; sans adresse, le nom de l'entree en tient lieu. Le
 * mot de passe compte tel quel, l'identifiant sans casse ni espaces autour (une adresse e-mail). La cle porte le mot
 * de passe : elle ne vit qu'en memoire et ne sort jamais dans le rapport.
 */
function duplicateKey(entry: VaultItem): string | null {
  const password = entry.secrets.password;
  if (typeof password !== 'string' || password.length === 0) return null;
  let site: string | null = null;
  for (const uri of entry.secrets.uris ?? []) {
    const host = siteHostname(uri);
    if (host === null) continue;
    site = registrableDomain(host) ?? host;
    break;
  }
  if (site === null) {
    if ((entry.secrets.uris ?? []).some((uri) => uri.trim().length > 0)) return null;
    const name = (entry.secrets.name ?? '').trim().toLowerCase();
    if (name.length === 0) return null;
    site = `nom:${name}`;
  }
  return JSON.stringify([entry.type, site, (entry.secrets.username ?? '').trim().toLowerCase(), password]);
}

/**
 * Entrees en double. Une entree archivee est gardee sciemment a part, une entree exclue ne leve aucune alerte : ni
 * l'une ni l'autre n'entre dans un groupe.
 */
function duplicateGroups(entries: readonly VaultItem[]): VaultDuplicateGroup[] {
  const groups = new Map<string, VaultItem[]>();
  for (const entry of entries) {
    if (isArchived(entry.secrets) || isHealthExcluded(entry.secrets)) continue;
    const key = duplicateKey(entry);
    if (key === null) continue;
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [entry]);
    else group.push(entry);
  }
  return [...groups.values()]
    .filter((group) => group.length > 1)
    .map((group) => ({ itemIds: [...group].sort((a, b) => b.updatedAt - a.updatedAt).map((entry) => entry.id) }))
    .sort((a, b) => b.itemIds.length - a.itemIds.length);
}

function penalty(issue: VaultHealthIssue): number {
  return (
    (issue.breachCount > 0 ? PENALTY.breached : 0) +
    (issue.weak ? PENALTY.weak : 0) +
    (issue.reused ? PENALTY.reused : 0) +
    (issue.old ? PENALTY.old : 0) +
    (issue.missingTotp ? PENALTY.missingTotp : 0) +
    (issue.insecureUri ? PENALTY.insecureUri : 0) +
    (issue.deceptiveUri ? PENALTY.deceptiveUri : 0) +
    (issue.siteBreach !== null ? PENALTY.compromisedSite : 0) +
    issue.secondaryPasswords.reduce(
      (sum, secondary) =>
        sum + (secondary.breachCount > 0 ? PENALTY.breached : 0) + (secondary.weak ? PENALTY.weak : 0) + (secondary.reused ? PENALTY.reused : 0),
      0,
    )
  );
}

export function computeVaultHealth(
  entries: readonly VaultItem[],
  now: number,
  /** Resultat de `checkPwnedPasswords` (cle = mot de passe dechiffre, en memoire uniquement). */
  breachCounts: ReadonlyMap<string, number> = new Map(),
  totpDirectory: TotpDirectory = defaultTotpDirectory(),
  passkeyDirectory: PasskeyDirectory = defaultPasskeyDirectory(),
  breachedSites: BreachedSiteDirectory = defaultBreachedSites(),
  /** E-mail du compte Klavox : sa partie locale rend faible, sur toute entree, le mot de passe qui la reprend. */
  accountEmail = '',
): VaultHealthReport {
  const accountWords = accountPasswordWords(accountEmail);
  const active = entries.filter((entry) => entry.deletedAt === null);
  const byId = new Map(active.map((entry) => [entry.id, entry]));
  const sites = vaultSites(entries);
  // L'analyse porte sur TOUT le coffre actif (une entree qui reutilise le mot de passe d'une exclue reste
  // signalee) ; seuls les constats des entrees exclues sont ecartes.
  const findings = analyzePasswordHygiene(active, accountWords).findings.filter((finding) => !isHealthExcluded(byId.get(finding.itemId)!.secrets));
  const findingOf = new Map(findings.map((finding) => [finding.itemId, finding]));
  // La reutilisation se compte sur TOUS les mots de passe : celui d'une console qui ouvre aussi un compte web l'est.
  const holders = passwordHolders(active);
  // Entrees analysees : celles qui ont un mot de passe, ou seulement un mot de passe secondaire (console sans compte).
  const analyzedEntries = active.filter((entry) => findingOf.has(entry.id) || (!isHealthExcluded(entry.secrets) && secondaryPasswords(entry).length > 0));
  const all = analyzedEntries.map((entry): VaultHealthIssue => {
    const finding = findingOf.get(entry.id);
    // Alertes que l'utilisateur a ignorees pour cette entree : jamais fuite, site compromis ni sosie (`health-exclusion.ts`).
    // « Faible » et « Reutilise » ignores valent pour tous les mots de passe de l'entree.
    const ignored = readIgnoredHealthAlerts(entry.secrets);
    const totpDocumentation = entry.secrets.totpSecret || ignored.includes('missingTotp') ? undefined : lookupSite(entry.secrets, totpDirectory);
    const secondary = secondaryPasswords(entry)
      .map(({ field, password }) => ({
        field,
        weak: !ignored.includes('weak') && estimatePasswordStrengthBits(password, [...passwordEntryWords(entry.secrets), ...accountWords]) < WEAK_PASSWORD_BITS_THRESHOLD,
        reused: !ignored.includes('reused') && (holders.get(password) ?? 0) > 1,
        breachCount: breachCounts.get(password) ?? 0,
      }))
      .filter((found) => found.weak || found.reused || found.breachCount > 0);
    const issue = {
      itemId: entry.id,
      weak: finding !== undefined && finding.weak && !ignored.includes('weak'),
      reused: finding !== undefined && (holders.get(entry.secrets.password ?? '') ?? 0) > 1 && !ignored.includes('reused'),
      // Dernier changement du mot de passe si l'historique le connait, sinon derniere modification de l'entree.
      old: finding !== undefined && !ignored.includes('old') && now - lastPasswordChange(entry.secrets, entry.updatedAt) > OLD_PASSWORD_AGE_MS,
      breachCount: finding === undefined ? 0 : breachCounts.get(entry.secrets.password ?? '') ?? 0,
      missingTotp: totpDocumentation !== undefined,
      totpDocumentation: totpDocumentation || null,
      insecureUri: !ignored.includes('insecureUri') && entryInClear(entry),
      deceptiveUri: findDeceptiveUri(entryAddresses(entry), sites) !== null,
      siteBreach: siteBreachSinceChange(entry, breachedSites),
    };
    return {
      ...issue,
      mustChange: issue.breachCount > 0 || issue.weak || issue.reused || issue.old || issue.siteBreach !== null,
      secondaryPasswords: secondary,
    };
  });
  // Une entree compte des qu'un de ses mots de passe (compte ou secondaire) porte le defaut.
  const count = (key: 'weak' | 'reused' | 'old' | 'missingTotp' | 'insecureUri' | 'deceptiveUri' | 'breachCount'): number =>
    all.filter((issue) => issue[key] || ((key === 'weak' || key === 'reused' || key === 'breachCount') && issue.secondaryPasswords.some((secondary) => secondary[key]))).length;
  const expiries = passwordExpiryIssues(active, now);
  const cards = cardIssues(active, now);
  const documents = documentIssues(active, now);
  const apiKeys = apiKeyIssues(active, now);
  const licenses = licenseIssues(active, now);
  const memberships = membershipIssues(active, now);
  const duplicates = duplicateGroups(active);
  // Memes entrees que l'analyse des mots de passe (actives, non exclues, avec un mot de passe).
  const passkeys = passkeyIssues(
    findings.map((finding) => byId.get(finding.itemId)!),
    passkeyDirectory,
  );
  const renamed = renamedSiteIssues(active);
  const score =
    all.length === 0 ? null : Math.round(all.reduce((sum, issue) => sum + Math.max(0, 100 - penalty(issue)), 0) / all.length);
  return {
    analyzed: all.length,
    weak: count('weak'),
    reused: count('reused'),
    old: count('old'),
    breached: count('breachCount'),
    missingTotp: count('missingTotp'),
    insecureUri: count('insecureUri'),
    deceptiveUri: count('deceptiveUri'),
    compromisedSite: all.filter((issue) => issue.siteBreach !== null).length,
    passkeyAvailable: passkeys.length,
    renamedSites: renamed.length,
    expiredPasswords: expiries.filter((expiry) => expiry.expired).length,
    expiringPasswords: expiries.filter((expiry) => !expiry.expired).length,
    expiredCards: cards.filter((card) => card.expired).length,
    expiringCards: cards.filter((card) => !card.expired).length,
    expiredDocuments: documents.filter((doc) => doc.expired).length,
    expiringDocuments: documents.filter((doc) => !doc.expired).length,
    expiredApiKeys: apiKeys.filter((key) => key.expired).length,
    expiringApiKeys: apiKeys.filter((key) => !key.expired).length,
    expiredLicenses: licenses.filter((license) => license.expired).length,
    expiringLicenses: licenses.filter((license) => !license.expired).length,
    expiredMemberships: memberships.filter((membership) => membership.expired).length,
    expiringMemberships: memberships.filter((membership) => !membership.expired).length,
    score,
    issues: all.filter((issue) => penalty(issue) > 0).sort((a, b) => penalty(b) - penalty(a)),
    passwordExpiryIssues: expiries,
    cardIssues: cards,
    documentIssues: documents,
    apiKeyIssues: apiKeys,
    licenseIssues: licenses,
    membershipIssues: memberships,
    passkeyIssues: passkeys,
    renamedSiteIssues: renamed,
    duplicateGroups: duplicates,
    duplicates: duplicates.reduce((sum, group) => sum + group.itemIds.length - 1, 0),
    excludedIds: active.filter((entry) => isHealthExcluded(entry.secrets)).map((entry) => entry.id),
    ignoredAlerts: active
      .filter((entry) => !isHealthExcluded(entry.secrets))
      .map((entry) => ({ itemId: entry.id, alerts: readIgnoredHealthAlerts(entry.secrets) }))
      .filter((ignored) => ignored.alerts.length > 0),
  };
}
